/**
 * walkImportSource — shared driver for the four import paths.
 *
 * Consumes any ImportSource's `files()` async iterator and produces an
 * ImportResult. Routes each file through the parser by extension:
 *
 *   .md          → parseVaultNote
 *   .flow        → parseFlowFile
 *   everything   → asset (kept as ImportFile in the result)
 *
 * Errors from one file never abort the import — they land in `errors`.
 */

import { parseFlowFile, parseVaultNote, type ParsedFlow, type ParsedNote } from '../parse';
import type { ImportError, ImportFile, ImportResult, ImportSource } from './types';

const TEXT_EXTS = new Set(['md', 'markdown', 'flow', 'txt', 'json']);

/**
 * Walk an import source and partition into notes / flows / assets.
 *
 * `onProgress` fires after each file lands; useful for the wizard's
 * progress bar. Cancellation: the AbortSignal is checked before each
 * file is processed.
 */
export async function walkImportSource(
  source: ImportSource,
  options: {
    signal?: AbortSignal;
    onProgress?: (processed: number, lastPath: string) => void;
  } = {},
): Promise<ImportResult> {
  const notes: ParsedNote[] = [];
  const flows: ParsedFlow[] = [];
  const assets: ImportFile[] = [];
  const errors: ImportError[] = [];
  let processed = 0;

  for await (const file of source.files()) {
    if (options.signal?.aborted) break;
    try {
      const ext = file.ext.toLowerCase();
      if (ext === 'md' || ext === 'markdown') {
        if (typeof file.text !== 'string') {
          errors.push({ path: file.path, message: 'markdown file missing text content' });
        } else {
          notes.push(parseVaultNote(file.path, file.text));
        }
      } else if (ext === 'flow') {
        if (typeof file.text !== 'string') {
          errors.push({ path: file.path, message: 'flow file missing text content' });
        } else {
          flows.push(parseFlowFile(file.path, file.text));
        }
      } else {
        assets.push(file);
      }
    } catch (err) {
      errors.push({
        path: file.path,
        message: (err as Error).message ?? String(err),
        cause: err,
      });
    }
    processed += 1;
    options.onProgress?.(processed, file.path);
  }

  return {
    notes,
    flows,
    assets,
    errors,
    sourceLabel: source.label,
    sourceKind: source.kind,
  };
}

/** Helper: classify by extension without parsing. Used by previews. */
export function classifyFile(path: string): 'note' | 'flow' | 'asset' {
  const dot = path.lastIndexOf('.');
  if (dot < 0) return 'asset';
  const ext = path.slice(dot + 1).toLowerCase();
  if (ext === 'md' || ext === 'markdown') return 'note';
  if (ext === 'flow') return 'flow';
  return 'asset';
}

/** Helper: whether a file extension should be read as text. */
export function isTextExt(ext: string): boolean {
  return TEXT_EXTS.has(ext.toLowerCase());
}
